/**
 * API client configuration using Ky
 */

import ky from 'ky';
import type { KyInstance } from 'ky';
import { env } from './env';
import { useStore } from '@/store';
import { ApiError } from './errors/api-error';

// Note: Security headers should be set on the server response, not client requests
// These will be configured in SST/CloudFront settings

class ApiClient {
  private client: KyInstance;
  private isRefreshing = false;
  private refreshPromise: Promise<void> | null = null;

  constructor() {
    this.client = ky.create({
      prefixUrl: env.api.url,
      timeout: 30000,
      credentials: 'include', // Always send cookies with requests
      retry: {
        limit: 3,
        methods: ['get', 'put', 'patch', 'delete'],
        statusCodes: [408, 413, 429, 500, 502, 503, 504],
        backoffLimit: 3000,
      },
      hooks: {
        beforeRequest: [
          async (request) => {
            // Check token expiry before request and attempt refresh
            const store = useStore.getState();
            if (store.isTokenExpired()) {
              await this.refreshAccessToken();
            }

            // Add auth token
            const token = useStore.getState().accessToken;
            if (token) {
              request.headers.set('Authorization', `Bearer ${token}`);
            }

            // Set content type
            if (!request.headers.has('Content-Type')) {
              request.headers.set('Content-Type', 'application/json');
            }
          },
        ],
        afterResponse: [
          async (request, _options, response) => {
            // Skip 401 handling for refresh endpoint to prevent infinite loops
            const isRefreshRequest = request.url.includes('auth/refresh');
            if (response.status === 401 && !isRefreshRequest) {
              const store = useStore.getState();

              // Try to refresh token once (if not already refreshing)
              if (!this.isRefreshing) {
                await this.refreshAccessToken();

                // Clone the original request with updated token
                const newRequest = request.clone();
                const token = useStore.getState().accessToken;
                if (token) {
                  newRequest.headers.set('Authorization', `Bearer ${token}`);
                }

                // Recreate the body if needed
                const body = await this.recreateBody(request);

                // Use this.client to maintain all configurations
                const url = newRequest.url.replace(env.api.url, '');
                return this.client(url, {
                  method: newRequest.method,
                  headers: newRequest.headers,
                  body: body,
                });
              }

              // If still 401 and user is authenticated, logout
              // But don't logout if already logged out (prevents infinite loop)
              if (store.isAuthenticated) {
                store.logout();
              }
            }
            return response;
          },
        ],
      },
    });
  }

  /**
   * Refresh access token
   */
  private async refreshAccessToken(): Promise<void> {
    // Prevent multiple simultaneous refresh attempts
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = (async () => {
      try {
        // Use cookie-based refresh with credentials: 'include'
        const response = await ky
          .post(`${env.api.url}/auth/refresh`, {
            credentials: 'include', // Send cookies with the request
            timeout: 10000,
          })
          .json<{ accessToken: string; expiresIn?: number; user?: any }>();

        const store = useStore.getState();
        store.updateTokens(response.accessToken, response.expiresIn);

        // Update user if returned
        if (response.user) {
          store.setUser(response.user);
        }
      } catch (error) {
        console.error('Token refresh failed:', error);
        const state = useStore.getState();
        // Only trigger logout if the user is currently authenticated
        // Avoids unnecessary logout calls during unauthenticated startup
        if (state.isAuthenticated) {
          state.logout();
        }
        throw error;
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * Recreate request body for retry
   */
  private async recreateBody(request: Request): Promise<any> {
    const contentType = request.headers.get('content-type');

    if (!request.body) {
      return undefined;
    }

    if (contentType?.includes('application/json')) {
      try {
        // Clone the request to avoid consuming the original body
        return await request.clone().json();
      } catch {
        return undefined;
      }
    }

    if (contentType?.includes('multipart/form-data')) {
      try {
        return await request.clone().formData();
      } catch {
        return undefined;
      }
    }

    // For other content types, try to clone the body
    try {
      return request.clone().body;
    } catch {
      return undefined;
    }
  }

  /**
   * GET request
   */
  async get<T = unknown>(url: string, options?: any): Promise<T> {
    try {
      return await this.client.get(url, options).json<T>();
    } catch (error) {
      throw await ApiError.fromKyError(error);
    }
  }

  /**
   * POST request
   */
  async post<T = unknown>(url: string, data?: any, options?: any): Promise<T> {
    try {
      return await this.client.post(url, { json: data, ...options }).json<T>();
    } catch (error) {
      throw await ApiError.fromKyError(error);
    }
  }

  /**
   * PUT request
   */
  async put<T = unknown>(url: string, data?: any, options?: any): Promise<T> {
    try {
      return await this.client.put(url, { json: data, ...options }).json<T>();
    } catch (error) {
      throw await ApiError.fromKyError(error);
    }
  }

  /**
   * PATCH request
   */
  async patch<T = unknown>(url: string, data?: any, options?: any): Promise<T> {
    try {
      return await this.client.patch(url, { json: data, ...options }).json<T>();
    } catch (error) {
      throw await ApiError.fromKyError(error);
    }
  }

  /**
   * DELETE request
   */
  async delete<T = unknown>(url: string, options?: any): Promise<T> {
    try {
      return await this.client.delete(url, options).json<T>();
    } catch (error) {
      throw await ApiError.fromKyError(error);
    }
  }

  /**
   * Stream request (for future SSE/streaming support)
   */
  async stream(url: string, options?: any): Promise<ReadableStream> {
    const response = await this.client.get(url, { ...options });
    if (!response.body) {
      throw new Error('No response body available for streaming');
    }
    return response.body;
  }
}

// Export a singleton instance
export const api = new ApiClient();

// Export typed API endpoints
export const apiEndpoints = {
  // Auth
  auth: {
    callback: (code: string) =>
      api.post<{ accessToken: string; user: any }>('auth/callback', { code }),
    refresh: () =>
      api.post<{ accessToken: string; expiresIn?: number; user?: any }>('auth/refresh'),
    logout: () => api.post('auth/logout'),
    me: () => api.get<{ user: any }>('me'),
  },

  // Organizations
  organizations: {
    setup: (data: { name: string; slug?: string; type?: 'standard' | 'agency' }) =>
      api.post('organizations/setup', { ...data, type: data.type || 'standard' }),
    getCurrent: () => api.get('organizations/current'),
    update: (organizationId: string, data: any) =>
      api.patch(`organizations/${organizationId}`, data),
    getWorkspaces: (organizationId: string) =>
      api.get(`organizations/${organizationId}/workspaces`),
    checkSlug: (slug: string) =>
      api.get(`organizations/check-slug?slug=${encodeURIComponent(slug)}`),
  },

  // Workspaces
  workspaces: {
    list: () => api.get('workspaces'),
    get: (id: string) => api.get(`workspaces/${id}`),
    create: (data: any) => api.post('workspaces', data),
    update: (id: string, data: any) => api.patch(`workspaces/${id}`, data),
    delete: (id: string) => api.delete(`workspaces/${id}`),
    getMembers: (id: string) => api.get(`workspaces/${id}/members`),
    addMember: (id: string, data: any) => api.post(`workspaces/${id}/members`, data),
    removeMember: (id: string, memberId: string) =>
      api.delete(`workspaces/${id}/members/${memberId}`),
    getBrandDomains: (id: string) => api.get(`workspaces/${id}/brand-domains`),
    addBrandDomain: (id: string, data: { domain: string }) =>
      api.post(`workspaces/${id}/brand-domains`, data),
    removeBrandDomain: (id: string, domainId: string) =>
      api.delete(`workspaces/${id}/brand-domains/${domainId}`),
    getDashboardMetrics: (id: string) => api.get(`workspaces/${id}/dashboard/metrics`),
  },

  // Campaigns
  campaigns: {
    list: (workspaceId: string) => api.get(`workspaces/${workspaceId}/campaigns`),
    get: (workspaceId: string, campaignId: string) =>
      api.get(`workspaces/${workspaceId}/campaigns/${campaignId}`),
    create: (workspaceId: string, data: any) =>
      api.post(`workspaces/${workspaceId}/campaigns`, data),
    update: (workspaceId: string, campaignId: string, data: any) =>
      api.patch(`workspaces/${workspaceId}/campaigns/${campaignId}`, data),
    delete: (workspaceId: string, campaignId: string) =>
      api.delete(`workspaces/${workspaceId}/campaigns/${campaignId}`),
  },

  // Example resource endpoints (kept for backwards compatibility)
  users: {
    list: () => api.get('users'),
    get: (id: string) => api.get(`users/${id}`),
    update: (id: string, data: any) => api.patch(`users/${id}`, data),
    delete: (id: string) => api.delete(`users/${id}`),
  },

  // Submissions (workspace-scoped)
  submissions: {
    list: (workspaceId: string, query?: Record<string, any>) =>
      api.get(`workspaces/${workspaceId}/submissions${toQuery(query)}`),
    get: (workspaceId: string, submissionId: string) =>
      api.get(`workspaces/${workspaceId}/submissions/${submissionId}`),
    getHtml: (workspaceId: string, submissionId: string) =>
      api.get<{ html: string; subject: string | null; fromAddress: string | null }>(
        `workspaces/${workspaceId}/submissions/${submissionId}/html`
      ),
    rerun: (workspaceId: string, submissionId: string) =>
      api.post(`workspaces/${workspaceId}/submissions/${submissionId}/rerun`),
    delete: (workspaceId: string, submissionId: string) =>
      api.delete(`workspaces/${workspaceId}/submissions/${submissionId}`),
  },

  // Billing (Paddle hosted checkout)
  billing: {
    createCheckout: (plan: string, billingCycle: 'monthly' | 'yearly') =>
      api.post('organizations/billing/checkout', { plan, billingCycle }),
  },
} as const;

// Helper to build query strings
function toQuery(query?: Record<string, any>) {
  if (!query) return '';
  const params = new URLSearchParams();
  Object.entries(query).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    params.append(k, String(v));
  });
  const s = params.toString();
  return s ? `?${s}` : '';
}
